import React, { useLayoutEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { PageSection, SectionBody, SectionHeader } from './PageSection';
import Link from './Link';
import { Heading, Text } from 'grommet';
import { useResize } from '../hooks/useResize';
import { getRefElement } from '../utilities/getRefElement';

const DrawerCTA = styled.div`
  text-align: center;
  padding: 1rem;
  a:hover {
    color: inherit;
  }
  flex: 0 0 auto;
`;
const DrawerBody = styled.div`
  padding: 1rem 1.5rem;
  /* padding-top: 0rem; */
  flex: 1;
`;

const DrawerTitle = styled.div`
  padding: 1rem 1.5rem;
  display: grid;
  align-items: center;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto;
  grid-gap: 1rem;
  flex: 0 0 auto;
  transition: background-color 0.3s ease;

  background-color: ${({ theme, $alternate }) =>
    $alternate ? 'black' : theme.global.colors['brand']};

  @media (hover: hover) {
    &:hover {
      background-color: ${({ $alternate, theme }) =>
        $alternate ? theme.global.colors['brand'] : 'black'};
    }
  }
`;

const Drawer = styled.div`
  cursor: pointer;
  z-index: 99;
  left: -50%; // compensate for the flex layout it's really in
  overflow: hidden;
  position: relative;
  background-color: ${({ theme }) => theme.global.colors['brand']};

  display: flex;
  flex-direction: column;

  transform: ${({ $isExpanded, $toggleHeight }) =>
    $isExpanded ? '0%' : `translateY(calc(100% - ${$toggleHeight}px))`};
  transition: transform 0.3s ease;
`;

const DrawerIcon = styled.div`
  width: 22px;
  height: 22px;
  display: block;
  cursor: pointer;
  position: relative;
  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    width: 22px;
    height: 4px;
    border-radius: 2px;
    background-color: ${({ theme }) => theme.global.colors['light-1']};
    transition: transform 0.3s ease;
  }
  ${({ isExpanded }) => {
    if (!isExpanded)
      return css`
        &:after {
          transform: translate(-50%, -50%) rotate(90deg);
        }
      `;
  }}
`;

const StyledExpandingCard = styled.div`
  background-color: ${({ theme }) => theme.global.colors['light-3']};
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: 0 0 var(--gutters) var(--gutters);
  min-width: 250px;
  /* one column */
  max-width: 100%;
  flex: 1;
  

  /* two columns */
  @media (min-width: 572px) {
    max-width: calc((100% / 2) - var(--gutters));
    flex: 1 0 calc(50% - (var(--gutters) * 2));
  }

  /* three columns */
  @media (min-width: 830px) {
    max-width: calc((100% / 3) - var(--gutters));
    flex: 1 0 calc(33% - (var(--gutters) * 2));
  }

  /* four columns */
  // @media (min-width: 1168px) {
  //   max-width: calc((100% / 4) - var(--gutters));
  //   flex: 1 0 calc(25% - (var(--gutters) * 2));
  // }
`;


const CardBody = styled.div`
  padding: 1.5rem;
  padding-top: 0rem;
  padding-bottom: ${({ $toggleHeight }) => `calc(1rem + ${$toggleHeight}px)`};
`;

const CardTitle = styled.div`
  z-index: 3;
  padding: 1.5rem;
`;

const CardContent = styled.div`
  /**
    This allows us to size the body and
    drawer according to whichever is largest.
  */
  display: flex;
  align-items: stretch;
  height: 100%;
  width: 200%;

  > div {
    height: 100%;
    flex: 0 0 50%;
  }
`;

const ExpandingCard = ({
  title,
  body,
  drawerTitle,
  drawerBody,
  ctaLabel,
  ctaUrl,
  alternate,
}) => {
  const [isExpanded, setExpanded] = useState(false);
  const [toggleHeight, setToggleHeight] = useState(0);

  const titleRef = useRef();
  const toggleRef = useRef();

  useLayoutEffect(() => {
    setToggleHeight(getRefElement(toggleRef).offsetHeight);
  }, []);

  useResize(() => {
    setToggleHeight(getRefElement(toggleRef).offsetHeight);
  });

  return (
    <StyledExpandingCard>
      {/* Card Title */}
      <CardTitle ref={titleRef}>
        <Heading level="4">{title}</Heading>
      </CardTitle>

      <CardContent>
        {/* Card Body */}
        <CardBody $toggleHeight={toggleHeight}>
          <Text>{body}</Text>
        </CardBody>
        {/* Drawer */}
        <Drawer $isExpanded={isExpanded} $toggleHeight={toggleHeight}>
          {/* Toggle Title */}
          <DrawerTitle
            ref={toggleRef}
            onClick={() => setExpanded(!isExpanded)}
            $alternate={alternate}
          >
            <Heading color={'light-1'} level="4">
              {drawerTitle}
            </Heading>
            <DrawerIcon isExpanded={isExpanded} />
          </DrawerTitle>

          {/* Collapsing Drawer Area */}
          <DrawerBody>
            <Text size="small" color={'light-1'}>{drawerBody}</Text>
          </DrawerBody>
          <DrawerCTA>
            <Text size="small" color="light-1">
              <Link onDark href={ctaUrl}>
                {ctaLabel}
              </Link>
            </Text>
          </DrawerCTA>
        </Drawer>
      </CardContent>
    </StyledExpandingCard>
  );
};

ExpandingCard.defaultProps = {
  title: 'Eos rerum dolor',
  body: 'Sed vel dolores iste et reiciendis aut. Suscipit dolor doloremque nostrum dolorem qui perferendis. Officiis accusamus exercitationem adipisci corporis sed dignissimos reiciendis.',
  drawerBody:
    'Provident illo inventore veritatis est architecto similique qui quam. Ea qui non eveniet consequatur eum ea. Ut omnis aut tenetur eius ut quos fugiat eius.',
  drawerTitle: 'Our solution',
  ctaUrl: '#',
  ctaLabel: 'Learn more',
  alternate: false,
};

const StyledExpandedCardsContainer = styled.div`
  overflow: hidden;
`;

const StyledExpandingCards = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  align-content: flex-start;
  flex-direction: row;
  margin-left: calc(0px - var(--gutters));
`;

const ExpandingCardSection = ({ title, subtitle, items }) => {
  return (
    <PageSection>
      <SectionHeader title={title} subtitle={subtitle} />
      <SectionBody>
        <StyledExpandedCardsContainer>
          <StyledExpandingCards>
            {items.map((item, i) => {
              return <ExpandingCard key={i} {...item} />;
            })}
          </StyledExpandingCards>
        </StyledExpandedCardsContainer>
      </SectionBody>
    </PageSection>
  );
};

ExpandingCardSection.propTypes = {};

ExpandingCardSection.defaultProps = {
  title: 'title',
  subtitle: null,
  items: [
    ExpandingCard.defaultProps,
    ExpandingCard.defaultProps,
    ExpandingCard.defaultProps,
    ExpandingCard.defaultProps,
    ExpandingCard.defaultProps,
    ExpandingCard.defaultProps,
  ],
};

export default ExpandingCardSection;
